import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import EditIcon from "@mui/icons-material/Edit";
import {
	Box,
	Chip,
	CircularProgress,
	IconButton,
	Switch,
	Tooltip,
	Typography,
} from "@mui/material";
import { DataGrid, GridColDef, csCZ } from "@mui/x-data-grid";
import { InstitutionDownloadConfigDto } from "Infrastructure/Api/Api";
import { useState } from "react";
import { useUpdateDownloadConfigMutation } from "../../Api/Mutations/useUpdateDownloadConfigMutation";
import { useTriggerDownloadMutation } from "../../Api/Mutations/useTriggerDownloadMutation";
import { DownloadResultDialog } from "./DownloadResultDialog";

interface DownloadConfigTableProps {
	configs: InstitutionDownloadConfigDto[];
	totalCount: number;
	page: number;
	pageSize: number;
	isLoading: boolean;
	onPageChange: (page: number) => void;
	onPageSizeChange: (pageSize: number) => void;
	onEdit: (config: InstitutionDownloadConfigDto) => void;
}

function formatDate(dateString: string | null | undefined): string {
	if (!dateString) return "-";
	return new Date(dateString).toLocaleString("cs-CZ", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

type DownloadResult = {
	downloaderName: string;
	files: { path: string; downloadUrl: string }[];
};

export const DownloadConfigTable: React.FC<DownloadConfigTableProps> = ({
	configs,
	totalCount,
	page,
	pageSize,
	isLoading,
	onPageChange,
	onPageSizeChange,
	onEdit,
}) => {
	const [downloadResult, setDownloadResult] = useState<DownloadResult | null>(
		null,
	);
	const [downloadingId, setDownloadingId] = useState<number | null>(null);

	const updateMutation = useUpdateDownloadConfigMutation();
	const triggerMutation = useTriggerDownloadMutation();

	const handleToggleEnabled = (config: InstitutionDownloadConfigDto) => {
		updateMutation.mutate({
			id: config.institutionDownloadConfigID,
			data: {
				enabled: !config.enabled,
				clearConfiguration: false,
			},
		});
	};

	const handleTriggerDownload = async (
		config: InstitutionDownloadConfigDto,
	) => {
		setDownloadingId(config.institutionDownloadConfigID);
		try {
			const response = await triggerMutation.mutateAsync(
				config.institutionDownloadConfigID,
			);
			if (response.status === 200 && response.data.success) {
				const files = response.data.uploadedPaths.map((path, index) => ({
					path,
					downloadUrl: response.data.downloadUrls[index],
				}));
				setDownloadResult({
					downloaderName: config.downloaderName,
					files,
				});
			}
		} catch {
			// Error handled by mutation's onError callback
		} finally {
			setDownloadingId(null);
		}
	};

	const columns: GridColDef[] = [
		{
			field: "downloaderName",
			headerName: "Název",
			flex: 1,
			minWidth: 120,
			sortable: false,
			renderCell: (params) => (
				<Typography variant="body2" fontWeight={500}>
					{params.value}
				</Typography>
			),
		},
		{
			field: "institutionCode",
			headerName: "Kód",
			width: 100,
			sortable: false,
			renderCell: (params) => (
				<Typography variant="body2" color="text.secondary">
					{params.value}
				</Typography>
			),
		},
		{
			field: "category",
			headerName: "Kategorie",
			width: 100,
			sortable: false,
			renderCell: (params) => (
				<Chip size="small" label={params.value} variant="outlined" sx={{ height: 22 }} />
			),
		},
		{
			field: "folderPath",
			headerName: "Cesta",
			flex: 1.5,
			minWidth: 200,
			sortable: false,
			renderCell: (params) => (
				<Typography
					variant="caption"
					sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
				>
					{params.value}
				</Typography>
			),
		},
		{
			field: "lastRunAt",
			headerName: "Poslední běh",
			width: 140,
			sortable: false,
			renderCell: (params) => (
				<Typography variant="body2">
					{formatDate(params.value as string | null)}
				</Typography>
			),
		},
		{
			field: "lastRunStatus",
			headerName: "Status",
			width: 120,
			sortable: false,
			renderCell: (params) => {
				const row = params.row as InstitutionDownloadConfigDto;
				const status = params.value as string | null;
				if (!status) return null;

				const isSuccess = status === "Success";
				const isFailed = status.startsWith("Failed") || status.startsWith("Skipped");
				const httpCode = row.lastRunHttpCode;
				const exception = row.lastRunException;

				const getLabel = () => {
					if (isSuccess) return httpCode ? `OK (${httpCode})` : "OK";
					if (isFailed) return httpCode ? `Chyba (${httpCode})` : "Chyba";
					return status;
				};

				const chip = (
					<Chip
						size="small"
						label={getLabel()}
						color={isSuccess ? "success" : isFailed ? "error" : "default"}
						variant="outlined"
						sx={{ height: 22, cursor: exception ? "help" : "default" }}
					/>
				);

				if (exception) {
					return (
						<Tooltip
							title={
								<Typography variant="caption" sx={{ whiteSpace: "pre-wrap", maxWidth: 400 }}>
									{exception.length > 500 ? `${exception.substring(0, 500)}...` : exception}
								</Typography>
							}
							arrow
							placement="left"
						>
							{chip}
						</Tooltip>
					);
				}

				return chip;
			},
		},
		{
			field: "enabled",
			headerName: "Aktivní",
			width: 80,
			align: "center",
			headerAlign: "center",
			sortable: false,
			renderCell: (params) => {
				const row = params.row as InstitutionDownloadConfigDto;
				return (
					<Switch
						size="small"
						checked={params.value as boolean}
						onChange={() => handleToggleEnabled(row)}
						disabled={updateMutation.isPending}
					/>
				);
			},
		},
		{
			field: "actions",
			headerName: "Akce",
			width: 100,
			align: "right",
			headerAlign: "right",
			sortable: false,
			renderCell: (params) => {
				const row = params.row as InstitutionDownloadConfigDto;
				const isDownloading = downloadingId === row.institutionDownloadConfigID;

				return (
					<>
						<Tooltip title="Upravit konfiguraci">
							<IconButton size="small" onClick={() => onEdit(row)}>
								<EditIcon fontSize="small" />
							</IconButton>
						</Tooltip>
						<Tooltip title="Spustit stahování">
							<IconButton
								size="small"
								onClick={() => handleTriggerDownload(row)}
								disabled={downloadingId !== null}
								color="primary"
							>
								{isDownloading ? (
									<CircularProgress size={18} />
								) : (
									<CloudDownloadIcon fontSize="small" />
								)}
							</IconButton>
						</Tooltip>
					</>
				);
			},
		},
	];

	if (!isLoading && configs.length === 0 && page === 0) {
		return (
			<Box sx={{ textAlign: "center", py: 4 }}>
				<Typography color="text.secondary">
					Žádné konfigurace ke zobrazení
				</Typography>
			</Box>
		);
	}

	return (
		<>
			<Box
				sx={{
					height: 500,
					width: "100%",
					"& .MuiDataGrid-root": {
						border: "1px solid",
						borderColor: "divider",
						borderRadius: 1,
						boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
					},
					"& .MuiDataGrid-columnHeaders": {
						backgroundColor: "white",
						borderBottom: "1px solid",
						borderColor: "divider",
					},
					"& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
						outline: "none",
					},
					"& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
						outline: "none",
					},
					"& .MuiDataGrid-columnHeaderTitle": {
						fontWeight: 700,
					},
				}}
			>
				<DataGrid
					rows={configs}
					columns={columns}
					getRowId={(row) => row.institutionDownloadConfigID}
					loading={isLoading}
					paginationMode="server"
					rowCount={totalCount}
					page={page}
					pageSize={pageSize}
					onPageChange={onPageChange}
					onPageSizeChange={onPageSizeChange}
					rowsPerPageOptions={[10, 25, 50]}
					disableSelectionOnClick
					disableColumnMenu
					autoHeight={false}
					density="compact"
					localeText={csCZ.components.MuiDataGrid.defaultProps.localeText}
				/>
			</Box>

			<DownloadResultDialog
				open={downloadResult !== null}
				onClose={() => setDownloadResult(null)}
				downloaderName={downloadResult?.downloaderName ?? ""}
				files={downloadResult?.files ?? []}
			/>
		</>
	);
};
